import React from 'react';
import { flag } from '../../config';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getUserRole,
  getDisplayName,
  getUserInitials,
  getDashboardRoles,
  ROLES,
  ROLE_COLORS,
  ROLE_ROUTES,
  ROLE_LABELS,
  ROLE_CTA,
} from './utils/roleUtils';
import MobileNavUserCard from './MobileNavUserCard';
import MobileNavPrimaryAction from './MobileNavPrimaryAction';
import MobileNavQuickStats from './MobileNavQuickStats';
import MobileNavLinks from './MobileNavLinks';

/**
 * Logged In Navigation State
 * 
 * Adapts based on user role:
 * - Consumer: My Codes focus, Taps display
 * - Business: Dashboard focus, Claims/Redeemed stats
 * - Venue: Dashboard focus, Scan stats
 * - Admin: Master Dashboard, Platform stats
 * 
 * For multi-role users (e.g., Admin + Business Owner):
 * - Shows side-by-side dashboard buttons
 */
export default function MobileNavLoggedIn({ user, onClose }) {
  const { logout } = useAuth();
  
  // Determine user's primary role (for display purposes)
  const primaryRole = getUserRole(user);
  const colors = ROLE_COLORS[primaryRole];
  const dashboardRoute = ROLE_ROUTES[primaryRole];
  const roleLabel = ROLE_LABELS[primaryRole];
  const cta = ROLE_CTA[primaryRole];
  
  // Get all dashboard-worthy roles (Admin, Venue, Business)
  const dashboardRoles = getDashboardRoles(user);
  
  // Get display info
  const displayName = getDisplayName(user, primaryRole);
  const initials = getUserInitials(user);
  const email = user?.email;
  
  // Handle logout
  const handleLogout = async () => {
    await logout();
    onClose();
  };
  
  // Determine if user is a consumer-only (no dashboard roles)
  const isConsumerOnly = dashboardRoles.length === 0;
  
  return (
    <>
      {/* User Profile Card */}
      <MobileNavUserCard
        initials={initials}
        displayName={displayName}
        email={email}
        role={primaryRole}
        roleLabel={roleLabel}
        colors={colors}
        taps={flag('REWARDS') ? user?.taps_balance : undefined}
        // Show multiple role badges if user has multiple roles
        allRoles={dashboardRoles.length > 1 ? dashboardRoles : null}
      />
      
      {/* Primary Action - Single or Multi-Role Buttons */}
      <MobileNavPrimaryAction
        to={dashboardRoute}
        title={cta.title}
        subtitle={cta.subtitle}
        role={primaryRole}
        colors={colors}
        badge={primaryRole === ROLES.CONSUMER ? user?.active_codes_count : null}
        onClose={onClose}
        dashboardRoles={dashboardRoles}
      />
      
      {/* Secondary action for consumers or consumer-only users. Under SLOT_MODEL the drawer's
          audience links (MobileNavAudienceLinks) already lead to the deals page. */}
      {(primaryRole === ROLES.CONSUMER || isConsumerOnly) && !flag('SLOT_MODEL') && (
        <Link
          to="/local-deals-near-me"
          onClick={onClose}
          className="block rounded-xl bg-white/5 border border-white/10 p-4 mb-4 hover:bg-white/10 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <span className="text-xl">🎁</span>
            </div>
            <div className="flex-1">
              <div className="font-display text-white">Find More Deals</div>
              <div className="text-xs text-white/50">Discover nearby offers</div>
            </div>
            <svg className="w-5 h-5 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>
      )}
      
      {/* Quick Stats (non-consumer roles) */}
      {primaryRole !== ROLES.CONSUMER && (
        <MobileNavQuickStats role={primaryRole} user={user} />
      )}
      
      {/* Secondary Links */}
      <MobileNavLinks role={primaryRole} user={user} onClose={onClose} />
      
      {/* Logout Button */}
      <button
        onClick={handleLogout}
        className="w-full py-3 rounded-xl border border-white/10 text-white/50 text-sm font-medium hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400 transition-all"
      >
        Log Out
      </button>
    </>
  );
}
